import { ToolbarButton } from '@fluentui/react-components';
import {
    ArrowSyncRegular,
    DocumentCopyRegular,
    DocumentDismissRegular,
} from '@fluentui/react-icons';
import { useLocation } from '@tanstack/react-router';
import { useRef, useState } from 'react';
import { useIsMounted } from 'usehooks-ts';
import { DocToolbarLink } from '../documentation/DocToolbarLink';
import type { IQueryListProps } from '../list/QueryList';
import {
    triggerClearMessages,
    triggerErrorMessage,
    triggerSuccessMessage,
} from '../message-bar-group/MessageBarGroup';
import {
    type IResetProcessDefinitionResult,
    resetProcess,
    resetProcessDefinition,
} from '../process-definition/reset-process-definition';
import { confirmResetProcessDefinition } from '../process-definition/confirm-reset-process-definition';
import { useProcessDefinitionsQueryXml } from '../process-definitions/process-definitions-query';
import { ToolbarOverflowGroup } from '../toolbar/ToolbarOverflowGroup';
import {
    processDefinitionsArchivedRouteApi,
    useProcessDefinitionsArchivedAnySelected,
    useProcessDefinitionsArchivedResetSelectionState,
    useProcessDefinitionsArchivedSelectionState,
} from './process-definitions-archived-api.ts';
import {
    useProcessDefinitionsArchivedDelete,
    useProcessDefinitionsArchivedQueryFragment,
    useProcessDefinitionsArchivedQueryRefresh,
} from './process-definitions-archived-query.ts';
import { triggerConfirmationDialog } from '../dialogs/ConfirmationDialog';

const ButtonRefresh = () => (
    <ToolbarButton
        aria-label="Liste mit archivierten Prozessdefinitionen aktualisieren"
        icon={<ArrowSyncRegular />}
        onClick={useProcessDefinitionsArchivedQueryRefresh()}
        title="Liste aktualisieren"
    />
);

const itemCopy = new ToolbarOverflowGroup.Item(() => {
    const selectionState = useProcessDefinitionsArchivedSelectionState(
        useProcessDefinitionsArchivedQueryFragment()
    );
    const prevPath = useLocation({ select: (state) => state.pathname });
    const navigate = processDefinitionsArchivedRouteApi.useNavigate();
    const [busy, setBusy] = useState<boolean | void>();
    const loadXml = useProcessDefinitionsQueryXml();
    const mountRef = useRef(useIsMounted());

    return {
        'aria-label': 'Ausgewählte (archivierte) Prozessdefinition kopieren',
        children: 'Kopieren',
        disabled: busy || !selectionState || selectionState.length !== 1,
        icon: <DocumentCopyRegular />,
        onClick: () => {
            setBusy(true);
            triggerClearMessages();
            loadXml(selectionState![0] as string)
                .then((result) => {
                    if (!result || !result.definitionXml) {
                        throw new Error('XML-Dokument fehlt');
                    }
                    setBusy();
                    return new Promise<IResetProcessDefinitionResult>((resolve, reject) => {
                        confirmResetProcessDefinition((reset) => {
                            (reset
                                ? resetProcessDefinition(result.definitionXml)
                                : resetProcess(result.definitionXml)
                            )
                                .then(resolve)
                                .catch(reject);
                        });
                    });
                })
                .then((resetResult) => {
                    if (mountRef.current()) {
                        navigate({
                            params: { id: '-1' },
                            //@ts-ignore
                            search: (prevSearch) => ({ prevPath, prevSearch }),
                            state: { xml: resetResult.xml } as any,
                            to: '/process-definition/$id',
                        });
                    }
                })
                .catch((error: Error) => {
                    triggerErrorMessage(error.message, 'Fehler beim Kopieren');
                    setBusy();
                });
        },
    };
});

const itemDelete = new ToolbarOverflowGroup.Item(() => {
    const refresh = useProcessDefinitionsArchivedQueryRefresh();
    const [commit, isInFlight] = useProcessDefinitionsArchivedDelete();
    const selectionState = useProcessDefinitionsArchivedSelectionState(
        useProcessDefinitionsArchivedQueryFragment()
    );
    const resetSelectionState = useProcessDefinitionsArchivedResetSelectionState();

    return {
        'aria-label': 'Ausgewählte Definitionen löschen',
        children: 'Löschen',
        disabled: !useProcessDefinitionsArchivedAnySelected(selectionState) || isInFlight,
        icon: <DocumentDismissRegular />,
        onClick: () => {
            triggerConfirmationDialog({
                confirmFn: () => {
                    triggerClearMessages();
                    const errTitle = 'Fehler beim Löschen';
                    commit({
                        onCompleted: (commitResult, commitErrors) => {
                            if (commitErrors && commitErrors.length > 0) {
                                triggerErrorMessage(commitErrors, errTitle);
                                return;
                            }

                            if (commitResult.deleteProcessDefinitions === false) {
                                triggerErrorMessage(
                                    'Definition(en) konnte(n) nicht gelöscht werden',
                                    errTitle
                                );
                                return;
                            }

                            triggerSuccessMessage('Definition(en) erfolgreich gelöscht');
                            refresh();
                            resetSelectionState();
                        },
                        onError: (error) => {
                            triggerErrorMessage(error.message, errTitle);
                        },
                        variables: { ids: selectionState as string[] },
                    });
                },
                confirmText: 'Löschen',
                content: 'Möchten Sie die ausgewählten Vorlagen wirklich löschen?',
                title: 'Vorlage(n)) löschen',
            });
        },
    };
});

export const toolbarItems: IQueryListProps['toolbar'] = {
    farItems: [<DocToolbarLink key="dl" />],
    items: [<ButtonRefresh key="br" />],
    overflowItems: [itemCopy, itemDelete],
} as const;
